import { forwardRef, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ClipboardListIcon } from 'lucide-react';
import { useFunnel } from '@tinct/web-funnel';
import { AiChat } from '@tinct/web-ai';
import { ApiError, useAddConfirmedStep, useGetCampaignById, useStartLandingPageGeneration } from '@tinct/core-api';
import { useCurrentWorkspaceUrlPrefix } from '@tinct/core-workspace';
import { pluralize } from '@tinct/core-utils';
import { Badge, Button } from '@tinct/web-components';
import { createDefaultLeadForm, type UpdateLeadForm } from '@tinct/core-api-models';
import { LeadFormDialog } from '../../../components/lead-form';
import { CampaignCreationFunnelState } from '../../campaign-creation-funnel/campaign-creation-funnel';
import { useCampaignChat } from '../../campaign-creation-funnel/hooks/use-campaign-chat';
import { createCampaignContextBlockPlugin } from '../../../plugins/campaign-context-block-plugin';
import { ChangesNotLiveModal } from '../../../modals/changes-not-live-modal/changes-not-live-modal';
import { useGenerateAllPages } from '../../../hooks/use-generate-all-pages';
import { campaignHasGeneratedPages } from '../../../utils/campaign-has-generated-pages';

export const Step1CampaignConfiguration = forwardRef<HTMLFormElement, object>((_, ref) => {
  const { state, navigateToNextStep } = useFunnel<CampaignCreationFunnelState>();
  const location = useLocation();
  const navigate = useNavigate();
  const currentWorkspaceUrlPrefix = useCurrentWorkspaceUrlPrefix();
  const campaignId = state.campaign.id as string;
  const [pendingMessage, setPendingMessage] = useState<string | null>(
    (location.state as { pendingMessage?: string })?.pendingMessage ?? null
  );
  const [showChangesNotLive, setShowChangesNotLive] = useState(false);

  const {
    messages,
    eventBus,
    isLoadingMore,
    hasMore,
    handleSendMessage,
    handleLoadMore,
  } = useCampaignChat({ campaignId, pendingMessage });

  const { mutateAsync: addConfirmedStep } = useAddConfirmedStep();

  const { data: campaign } = useGetCampaignById(
    {
      id: campaignId,
      fields: {
        id: null,
        status: null,
        statistics: {
          fields: {
            totalPagesCount: null,
          },
        },
      },
    },
    { enabled: !!campaignId }
  );
  const { generateAllAndReview } = useGenerateAllPages(campaignId);

  const pagesCount = campaign?.statistics?.totalPagesCount ?? 0;
  const isLiveWithPages = campaignHasGeneratedPages(campaign);

  // "Continue" keeps editing: send the user to the merged personalize &
  // preview step rather than advancing through the analysis steps again.
  const goToEditor = () => {
    setShowChangesNotLive(false);
    navigate(`${currentWorkspaceUrlPrefix}/funnel-campaigns/${campaignId}/preview`);
  };

  const goGenerateAndReview = async () => {
    // The quota gate lives in the hook: when credits fall short it opens the
    // paywall and skips `onStart`, leaving this modal up instead of navigating.
    await generateAllAndReview({ onStart: () => setShowChangesNotLive(false) });
  };

  const isNewOrigin = state.campaign.landingPageOriginType === 'new';
  const { mutateAsync: startLandingPageGeneration } = useStartLandingPageGeneration();

  // Lead-form draft, sent with the generation call so the FIRST generated
  // version renders the form. Null = never configured here → body omitted,
  // leaving any stored config untouched.
  const [leadFormDraft, setLeadFormDraft] = useState<UpdateLeadForm | null>(null);
  const [leadFormDialogOpen, setLeadFormDialogOpen] = useState(false);

  const campaignContextPlugin = useMemo(
    () =>
      createCampaignContextBlockPlugin({
        onConfirm: async () => {
          await addConfirmedStep({ id: campaignId, confirmedStep: 'value_proposition' });

          // NEW origin: kick off the page generation and advance to building.
          if (isNewOrigin) {
            try {
              await startLandingPageGeneration({
                id: campaignId,
                leadForm: leadFormDraft ?? undefined,
              });
            } catch (error) {
              const apiError = error as ApiError;
              // A run already in flight is fine — building owns the outcome.
              if (!/in.flight|already|running/i.test(apiError.error ?? '')) {
                // Most notably "no brand source site": the user skipped the
                // brand-check turn — drop them back into the chat to fix it.
                toast.error(
                  apiError.error || 'Could not start the page generation. Please try again.'
                );
                return;
              }
            }
            navigateToNextStep(true);
            return;
          }

          // Editing a live campaign's value proposition outdates its pages —
          // inform the user instead of advancing the funnel automatically.
          if (isLiveWithPages) {
            setShowChangesNotLive(true);
            return;
          }
          navigateToNextStep(true);
        },
        onStartOver: () => handleSendMessage('Let me start over'),
      }),
    [navigateToNextStep, handleSendMessage, addConfirmedStep, campaignId, isLiveWithPages, isNewOrigin, startLandingPageGeneration, leadFormDraft]
  );

  return (
    <div className="relative h-[calc(100vh-theme(size.16))] w-full">
      {/* Lead-form control — the config rides the generation call */}
      {isNewOrigin && (
        <div className="absolute right-6 top-4 z-10">
          <Button variant="outline" onClick={() => setLeadFormDialogOpen(true)}>
            <ClipboardListIcon className="size-4" />
            Lead form
            {leadFormDraft?.enabled && <Badge variant="secondary">On</Badge>}
          </Button>
        </div>
      )}
      <AiChat
        key={`campaign-chat-${campaignId}`}
        useDefaultPlugins={true}
        plugins={[campaignContextPlugin]}
        eventBus={eventBus}
        messages={messages}
        onSendMessage={handleSendMessage}
        onLoadMore={handleLoadMore}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        pendingMessage={pendingMessage}
        onPendingMessageConsumed={() => {
          setPendingMessage(null);
          navigate(location.pathname, { replace: true, state: {} });
        }}
      />
      <ChangesNotLiveModal
        open={showChangesNotLive}
        onOpenChange={(next) => !next && setShowChangesNotLive(false)}
        step="the value proposition"
        alertDescription={`All ${pagesCount} variant ${pluralize(
          pagesCount,
          'page'
        )} ${pluralize(pagesCount, 'needs', 'need')} to be updated.`}
        actions={[
          { label: 'Continue', onClick: goToEditor },
          { label: 'Generate & review', onClick: goGenerateAndReview },
        ]}
      />
      {isNewOrigin && (
        <LeadFormDialog
          open={leadFormDialogOpen}
          onOpenChange={setLeadFormDialogOpen}
          value={leadFormDraft ?? { ...createDefaultLeadForm(), enabled: true }}
          integrationsUrl={`${currentWorkspaceUrlPrefix}/settings/integrations`}
          onSave={(draft) => {
            setLeadFormDraft(draft);
            setLeadFormDialogOpen(false);
          }}
        />
      )}
    </div>
  );
});

Step1CampaignConfiguration.displayName = 'Step1CampaignConfiguration';
